import type { Prisma, PrismaClient } from '@prisma/client';
import Decimal from 'decimal.js';
import { Router, type Request } from 'express';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { randomToken } from '../../core/crypto.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, pageParams, parse } from '../../core/http.js';
import { idempotent } from '../../core/idempotency.js';
import { authenticate } from '../auth/auth.middleware.js';
import { csvLine } from '../csv/csv.parser.js';
import { isoOf, toDate } from '../sales/documents.service.js';
import {
  ACTIVITY_TYPES,
  DEFAULT_SCORE_RULES,
  LEAD_SOURCES,
  SERVICE_INTERESTS,
  activityView,
  can,
  computeScore,
  convertLead,
  createLead,
  ensureLeadRule,
  ensurePipelines,
  findLeadDuplicates,
  getScoreRules,
  leadInputSchema,
  leadView,
  loadLead,
  logActivity,
  moveLead,
  ownScope,
  pipelineInclude,
  pipelineInputSchema,
  savePipeline,
  scheduleLeadFollowUp,
  scoreRulesSchema,
  updateLead,
} from './crm.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

export function createCrmRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/crm', authenticate({ db, config }), idempotent(db));

  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });
  const need = (req: Request, p: string) => {
    if (!can(req.auth!, p))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
  };

  async function decorate(leads: Awaited<ReturnType<typeof db.lead.findMany>>, companyId: number) {
    const stages = await db.pipelineStage.findMany({ where: { pipeline: { companyId } } });
    const pipes = await db.pipeline.findMany({
      where: { companyId },
      select: { id: true, name: true },
    });
    const users = await db.user.findMany({
      where: { companyId },
      select: { id: true, name: true },
    });
    return leads.map((l) => {
      const st = stages.find((s) => s.id === l.stageId);
      return leadView(l, {
        ownerName: users.find((u) => u.id === l.ownerId)?.name,
        stageName: st?.name,
        stageKind: st?.kind,
        probability: st?.probability,
        pipelineName: pipes.find((p) => p.id === l.pipelineId)?.name,
      });
    });
  }

  // ───────────── owners (for assignment pickers) ─────────────
  router.get(
    '/crm/owners',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.view');
      const users = await db.user.findMany({
        where: {
          companyId: req.auth!.companyId,
          status: 'active',
          ...(req.auth!.scope === 'own' ? { id: req.auth!.userId } : {}),
        },
        select: { id: true, name: true },
        orderBy: { name: 'asc' },
      });
      res.json({ items: users });
    }),
  );

  // ───────────── pipelines ─────────────
  router.get(
    '/crm/pipelines',
    asyncHandler(async (req, res) => {
      need(req, 'crm.pipeline.view');
      await ensurePipelines(db, req.auth!.companyId);
      const rows = await db.pipeline.findMany({
        where: { companyId: req.auth!.companyId },
        include: pipelineInclude,
        orderBy: { position: 'asc' },
      });
      res.json({ items: rows });
    }),
  );
  router.post(
    '/crm/pipelines',
    asyncHandler(async (req, res) => {
      need(req, 'crm.pipeline.create');
      const input = parse(pipelineInputSchema, req.body);
      const p = await db.$transaction(async (tx) => {
        const created = await savePipeline(tx, req.auth!.companyId, input);
        await audit(tx, actor(req), {
          action: 'pipeline.created',
          entity: 'pipeline',
          entityId: created.id,
          after: { name: created.name },
        });
        return created;
      });
      res.status(201).json(p);
    }),
  );
  router.put(
    '/crm/pipelines/:id',
    asyncHandler(async (req, res) => {
      need(req, 'crm.pipeline.edit');
      const input = parse(pipelineInputSchema, req.body);
      const p = await db.$transaction(async (tx) => {
        const saved = await savePipeline(tx, req.auth!.companyId, input, Number(req.params.id));
        await audit(tx, actor(req), {
          action: 'pipeline.updated',
          entity: 'pipeline',
          entityId: saved.id,
          after: { name: saved.name, stages: saved.stages.map((s) => s.name) },
        });
        return saved;
      });
      res.json(p);
    }),
  );
  router.delete(
    '/crm/pipelines/:id',
    asyncHandler(async (req, res) => {
      need(req, 'crm.pipeline.delete');
      const id = Number(req.params.id);
      const p = await db.pipeline.findFirst({ where: { id, companyId: req.auth!.companyId } });
      if (!p) throw new HttpError(404, 'NOT_FOUND', 'Pipeline not found');
      const leads = await db.lead.count({ where: { pipelineId: id } });
      if (leads > 0)
        throw new HttpError(
          409,
          'PIPELINE_IN_USE',
          `This pipeline has ${leads} lead(s). Deactivate it instead.`,
        );
      await db.pipeline.delete({ where: { id } });
      await audit(db, actor(req), {
        action: 'pipeline.deleted',
        entity: 'pipeline',
        entityId: id,
        before: { name: p.name },
      });
      res.status(204).end();
    }),
  );

  // ───────────── leads: list / export ─────────────
  const listWhere = (req: Request): Prisma.LeadWhereInput => {
    const q = req.query as Record<string, string | undefined>;
    const search = q.search?.trim();
    return {
      AND: [
        { companyId: req.auth!.companyId, ...ownScope(req.auth!) },
        q.status ? { status: q.status } : {},
        q.pipelineId ? { pipelineId: Number(q.pipelineId) } : {},
        q.stageId ? { stageId: Number(q.stageId) } : {},
        q.source ? { source: q.source } : {},
        q.ownerId === 'me'
          ? { ownerId: req.auth!.userId }
          : q.ownerId
            ? { ownerId: Number(q.ownerId) }
            : {},
        q.followUp === 'today'
          ? {
              nextFollowUpDate: { lte: toDate(new Date().toISOString().slice(0, 10)) },
              status: 'open',
            }
          : {},
        q.needsReview === 'true' ? { needsReview: true } : {},
        search
          ? {
              OR: [
                { name: { contains: search } },
                { contactName: { contains: search } },
                { email: { contains: search } },
                { phone: { contains: search } },
              ],
            }
          : {},
      ],
    };
  };

  router.get(
    '/crm/leads',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.view');
      await ensurePipelines(db, req.auth!.companyId);
      const { page, pageSize, skip, take } = pageParams(
        req.query as Record<string, unknown>,
        50,
        500,
      );
      const where = listWhere(req);
      const sortKey = ['createdAt', 'name', 'estimatedValue', 'score', 'nextFollowUpDate'].includes(
        String(req.query.sort),
      )
        ? String(req.query.sort)
        : 'createdAt';
      const [total, rows] = await Promise.all([
        db.lead.count({ where }),
        db.lead.findMany({
          where,
          orderBy: { [sortKey]: req.query.dir === 'asc' ? 'asc' : 'desc' },
          skip,
          take,
        }),
      ]);
      res.json({ items: await decorate(rows, req.auth!.companyId), total, page, pageSize });
    }),
  );

  router.get(
    '/crm/leads/export.csv',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.export');
      const rows = await db.lead.findMany({ where: listWhere(req), orderBy: { id: 'asc' } });
      const view = await decorate(rows, req.auth!.companyId);
      res.setHeader('Content-Type', 'text/csv; charset=utf-8');
      res.setHeader('Content-Disposition', 'attachment; filename="leads.csv"');
      res.write(
        '﻿' +
          csvLine([
            'name',
            'contact_name',
            'phone',
            'email',
            'source',
            'service_interest',
            'estimated_value',
            'currency',
            'owner_email',
            'pipeline',
            'stage',
            'status',
            'lost_reason',
            'next_follow_up',
            'tags',
            'notes',
            'score',
          ]) +
          '\r\n',
      );
      const owners = new Map(
        (
          await db.user.findMany({
            where: { companyId: req.auth!.companyId },
            select: { id: true, email: true },
          })
        ).map((u) => [u.id, u.email]),
      );
      for (const l of view)
        res.write(
          csvLine([
            l.name,
            l.contactName,
            l.phone,
            l.email,
            l.source,
            l.serviceInterest,
            l.estimatedValue,
            l.currency,
            l.ownerId ? owners.get(l.ownerId) : '',
            l.pipelineName,
            l.stageName,
            l.status,
            l.lostReason,
            l.nextFollowUpDate,
            l.tags.join(';'),
            l.notes,
            l.score,
          ]) + '\r\n',
        );
      await db.exportLog.create({
        data: {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          entity: 'leads',
          filters: req.query as Prisma.InputJsonValue,
          rowCount: view.length,
        },
      });
      await audit(db, actor(req), {
        action: 'export.csv',
        entity: 'leads',
        after: { rows: view.length },
      });
      res.end();
    }),
  );

  router.get(
    '/crm/leads/duplicates',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.view');
      const q = req.query as Record<string, string | undefined>;
      res.json(
        await findLeadDuplicates(
          db,
          req.auth!.companyId,
          { name: q.name, email: q.email, phone: q.phone },
          q.exclude ? Number(q.exclude) : undefined,
        ),
      );
    }),
  );

  router.post(
    '/crm/leads/bulk',
    asyncHandler(async (req, res) => {
      const body = parse(
        z.object({
          ids: z.array(z.number().int().positive()).min(1).max(200),
          action: z.enum(['assign', 'move']),
          ownerId: z.number().int().positive().optional(),
          stageId: z.number().int().positive().optional(),
          lostReason: z.string().trim().max(300).optional(),
        }),
        req.body,
      );
      need(req, 'crm.lead.edit');
      if (body.action === 'assign' && !body.ownerId)
        throw new HttpError(400, 'VALIDATION_ERROR', 'Choose an owner');
      if (body.action === 'move' && !body.stageId)
        throw new HttpError(400, 'VALIDATION_ERROR', 'Choose a stage');
      const results: { id: number; ok: boolean; message: string }[] = [];
      for (const id of body.ids) {
        try {
          await db.$transaction(async (tx) => {
            if (body.action === 'assign')
              await updateLead(tx, req.auth!, id, { ownerId: body.ownerId });
            else await moveLead(tx, req.auth!, id, body.stageId!, body.lostReason);
          });
          const lead = await db.lead.findUniqueOrThrow({ where: { id } });
          await scheduleLeadFollowUp(db, lead);
          results.push({ id, ok: true, message: 'Done' });
        } catch (e) {
          results.push({ id, ok: false, message: e instanceof Error ? e.message : 'Failed' });
        }
      }
      await audit(db, actor(req), {
        action: 'lead.bulk',
        entity: 'lead',
        after: { action: body.action, ids: body.ids, ok: results.filter((r) => r.ok).length },
      });
      res.json({
        results,
        succeeded: results.filter((r) => r.ok).length,
        failed: results.filter((r) => !r.ok).length,
      });
    }),
  );

  // ───────────── leads: create / read / update ─────────────
  router.post(
    '/crm/leads',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.create');
      const input = parse(leadInputSchema, req.body);
      const lead = await db.$transaction(async (tx) => {
        const created = await createLead(tx, req.auth!, input);
        await audit(tx, actor(req), {
          action: 'lead.created',
          entity: 'lead',
          entityId: created.id,
          after: { name: created.name, source: created.source },
        });
        return created;
      });
      await scheduleLeadFollowUp(db, lead);
      const dupes = await findLeadDuplicates(
        db,
        req.auth!.companyId,
        { name: lead.name, email: lead.email, phone: lead.phone },
        lead.id,
      );
      res.status(201).json({ ...(await decorate([lead], lead.companyId))[0]!, duplicates: dupes });
    }),
  );

  router.get(
    '/crm/leads/:id',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.view');
      const lead = await loadLead(db, req.auth!, Number(req.params.id));
      const [view] = await decorate([lead], lead.companyId);
      const [acts, hist, tasks, pipeline] = await Promise.all([
        db.leadActivity.findMany({
          where: { leadId: lead.id },
          orderBy: { id: 'desc' },
          take: 200,
        }),
        db.leadStageHistory.findMany({ where: { leadId: lead.id }, orderBy: { id: 'asc' } }),
        db.task.findMany({
          where: { companyId: lead.companyId, relatedType: 'lead', relatedId: lead.id },
          orderBy: [{ status: 'asc' }, { dueDate: 'asc' }],
          take: 50,
        }),
        db.pipeline.findUnique({ where: { id: lead.pipelineId }, include: pipelineInclude }),
      ]);
      const users = await db.user.findMany({
        where: { companyId: lead.companyId },
        select: { id: true, name: true },
      });
      const name = (id: number | null) =>
        id ? (users.find((u) => u.id === id)?.name ?? null) : null;
      const dupes = await findLeadDuplicates(
        db,
        lead.companyId,
        { name: lead.name, email: lead.email, phone: lead.phone },
        lead.id,
      );
      res.json({
        ...view,
        pipeline: pipeline && { id: pipeline.id, name: pipeline.name, stages: pipeline.stages },
        activities: acts.map((a) => activityView(a, name(a.userId))),
        stageHistory: hist.map((h) => ({
          id: h.id,
          fromStageId: h.fromStageId,
          toStageId: h.toStageId,
          at: h.changedAt,
          by: name(h.changedBy),
        })),
        tasks: tasks.map((t) => ({
          id: t.id,
          title: t.title,
          dueDate: isoOf(t.dueDate),
          status: t.status,
          assigneeName: name(t.assigneeId),
        })),
        duplicates: dupes,
        canConvert: can(req.auth!, 'sales.client.create'),
        canQuote: can(req.auth!, 'sales.quotation.create'),
      });
    }),
  );

  router.put(
    '/crm/leads/:id',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.edit');
      const input = parse(leadInputSchema.partial(), req.body);
      const before = await loadLead(db, req.auth!, Number(req.params.id));
      const lead = await db.$transaction(async (tx) => {
        const saved = await updateLead(tx, req.auth!, before.id, input);
        await audit(tx, actor(req), {
          action: 'lead.updated',
          entity: 'lead',
          entityId: saved.id,
          before: { name: before.name, ownerId: before.ownerId },
          after: { name: saved.name, ownerId: saved.ownerId },
        });
        return saved;
      });
      await scheduleLeadFollowUp(db, lead);
      res.json((await decorate([lead], lead.companyId))[0]);
    }),
  );

  router.post(
    '/crm/leads/:id/move',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.edit');
      const body = parse(
        z.object({
          stageId: z.number().int().positive(),
          lostReason: z.string().trim().max(300).optional(),
        }),
        req.body,
      );
      const lead = await db.$transaction(async (tx) => {
        const moved = await moveLead(
          tx,
          req.auth!,
          Number(req.params.id),
          body.stageId,
          body.lostReason,
        );
        await audit(tx, actor(req), {
          action: 'lead.moved',
          entity: 'lead',
          entityId: moved.id,
          after: { stageId: moved.stageId, status: moved.status },
        });
        return moved;
      });
      await scheduleLeadFollowUp(db, lead);
      res.json((await decorate([lead], lead.companyId))[0]);
    }),
  );

  router.post(
    '/crm/leads/:id/activities',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.edit');
      const body = parse(
        z.object({
          type: z.enum(ACTIVITY_TYPES),
          note: z.string().trim().min(1).max(1000),
          nextFollowUpDate: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable()
            .optional(),
        }),
        req.body,
      );
      const lead = await loadLead(db, req.auth!, Number(req.params.id));
      await logActivity(db, lead.id, body.type, body.note, req.auth!.userId);
      if (body.nextFollowUpDate !== undefined) {
        const updated = await db.lead.update({
          where: { id: lead.id },
          data: {
            nextFollowUpDate: body.nextFollowUpDate ? toDate(body.nextFollowUpDate) : null,
            updatedBy: req.auth!.userId,
          },
        });
        await scheduleLeadFollowUp(db, updated);
      }
      res.status(201).json({ ok: true });
    }),
  );

  router.post(
    '/crm/leads/:id/convert',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.edit');
      need(req, 'sales.client.create');
      const body = parse(
        z.object({
          createQuotation: z.boolean().default(true),
          partyId: z.number().int().positive().optional(),
        }),
        req.body,
      );
      const createQuotation = body.createQuotation && can(req.auth!, 'sales.quotation.create');
      const out = await db.$transaction(async (tx) => {
        const r = await convertLead(tx, req.auth!, Number(req.params.id), {
          createQuotation,
          partyId: body.partyId,
        });
        await audit(tx, actor(req), {
          action: 'lead.converted',
          entity: 'lead',
          entityId: r.lead.id,
          after: { partyId: r.partyId, quotationId: r.quotationId },
        });
        return r;
      });
      res.json({ partyId: out.partyId, quotationId: out.quotationId });
    }),
  );

  // ───────────── tasks and follow-ups ─────────────
  router.get(
    '/crm/tasks',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.view');
      const q = req.query as Record<string, string | undefined>;
      const today = toDate(new Date().toISOString().slice(0, 10));
      const scopeAll = req.auth!.scope === 'all' && q.scope === 'team';
      const rows = await db.task.findMany({
        where: {
          companyId: req.auth!.companyId,
          status: q.status === 'done' ? 'done' : 'open',
          ...(scopeAll ? {} : { assigneeId: req.auth!.userId }),
          ...(q.when === 'today' ? { dueDate: { lte: today } } : {}),
        },
        orderBy: [{ dueDate: 'asc' }, { id: 'desc' }],
        take: 200,
      });
      const leadIds = rows
        .filter((t) => t.relatedType === 'lead')
        .map((t) => t.relatedId!)
        .filter(Boolean);
      const leads = await db.lead.findMany({
        where: { id: { in: leadIds } },
        select: { id: true, name: true },
      });
      const users = await db.user.findMany({
        where: { companyId: req.auth!.companyId },
        select: { id: true, name: true },
      });
      res.json({
        items: rows.map((t) => ({
          id: t.id,
          title: t.title,
          description: t.description,
          dueDate: isoOf(t.dueDate),
          overdue: !!t.dueDate && t.dueDate < today && t.status === 'open',
          status: t.status,
          relatedType: t.relatedType,
          relatedId: t.relatedId,
          relatedName:
            t.relatedType === 'lead' ? leads.find((l) => l.id === t.relatedId)?.name : undefined,
          assigneeName: users.find((u) => u.id === t.assigneeId)?.name ?? null,
        })),
      });
    }),
  );
  router.post(
    '/crm/tasks',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.edit');
      const body = parse(
        z.object({
          title: z.string().trim().min(2).max(300),
          dueDate: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable()
            .optional(),
          leadId: z.number().int().positive().optional(),
          assigneeId: z.number().int().positive().optional(),
        }),
        req.body,
      );
      if (body.leadId) await loadLead(db, req.auth!, body.leadId);
      if (body.assigneeId && body.assigneeId !== req.auth!.userId) {
        if (req.auth!.scope === 'own')
          throw new HttpError(403, 'FORBIDDEN', 'You can only create tasks for yourself');
        const u = await db.user.findFirst({
          where: { id: body.assigneeId, companyId: req.auth!.companyId, status: 'active' },
        });
        if (!u) throw new HttpError(400, 'VALIDATION_ERROR', 'Choose an active team member');
      }
      const t = await db.task.create({
        data: {
          companyId: req.auth!.companyId,
          assigneeId: body.assigneeId ?? req.auth!.userId,
          title: body.title,
          dueDate: body.dueDate ? toDate(body.dueDate) : null,
          relatedType: body.leadId ? 'lead' : null,
          relatedId: body.leadId ?? null,
          source: 'manual',
        },
      });
      if (body.leadId)
        await logActivity(db, body.leadId, 'task', `Task: ${body.title}`, req.auth!.userId);
      await audit(db, actor(req), {
        action: 'task.created',
        entity: 'task',
        entityId: t.id,
        after: { title: t.title },
      });
      res.status(201).json({ id: t.id });
    }),
  );

  // ───────────── scoring rules ─────────────
  router.get(
    '/crm/scoring',
    asyncHandler(async (req, res) => {
      need(req, 'crm.pipeline.view');
      res.json({
        rules: await getScoreRules(db, req.auth!.companyId),
        defaults: DEFAULT_SCORE_RULES,
        sources: LEAD_SOURCES,
        interests: SERVICE_INTERESTS,
      });
    }),
  );
  router.put(
    '/crm/scoring',
    asyncHandler(async (req, res) => {
      need(req, 'crm.pipeline.edit');
      const rules = parse(scoreRulesSchema, req.body?.rules);
      const companyId = req.auth!.companyId;
      await db.setting.upsert({
        where: { companyId_key: { companyId, key: 'leadScoring' } },
        create: { companyId, key: 'leadScoring', value: rules },
        update: { value: rules },
      });
      // Re-score open leads so the numbers match the new rules.
      const leads = await db.lead.findMany({ where: { companyId } });
      for (const l of leads) {
        const score = computeScore(
          {
            email: l.email,
            phone: l.phone,
            source: l.source,
            serviceInterest: l.serviceInterest,
            estimatedValue: l.estimatedValue.toString(),
            currency: l.currency,
          },
          rules,
        );
        if (score !== l.score) await db.lead.update({ where: { id: l.id }, data: { score } });
      }
      await audit(db, actor(req), {
        action: 'lead_scoring.updated',
        entity: 'settings',
        after: { rules: rules.length },
      });
      res.json({ ok: true });
    }),
  );

  // ───────────── website capture settings ─────────────
  const captureView = async (companyId: number) => {
    const row = await db.setting.findUnique({
      where: { companyId_key: { companyId, key: 'leadCapture' } },
    });
    const v =
      (row?.value as
        | {
            enabled?: boolean;
            key?: string;
            pipelineId?: number | null;
            ownerId?: number | null;
            allowedOrigins?: string[];
          }
        | undefined) ?? {};
    return {
      enabled: v.enabled ?? false,
      key: v.key ?? null,
      pipelineId: v.pipelineId ?? null,
      ownerId: v.ownerId ?? null,
      allowedOrigins: v.allowedOrigins ?? [],
      endpoint: `${config.APP_URL.replace(/\/$/, '')}/api/v1/public/leads`,
      captcha: config.TURNSTILE_SITE_KEY ? 'turnstile' : 'built-in',
    };
  };
  router.get(
    '/crm/capture',
    asyncHandler(async (req, res) => {
      need(req, 'crm.pipeline.view');
      res.json(await captureView(req.auth!.companyId));
    }),
  );
  router.put(
    '/crm/capture',
    asyncHandler(async (req, res) => {
      need(req, 'crm.pipeline.edit');
      const body = parse(
        z.object({
          enabled: z.boolean(),
          pipelineId: z.number().int().positive().nullable(),
          ownerId: z.number().int().positive().nullable(),
          allowedOrigins: z
            .array(
              z
                .string()
                .url()
                .max(200)
                .refine(
                  (u) => /^https?:\/\/[^/]+$/.test(u),
                  'Use the site address only, e.g. https://www.example.com',
                ),
            )
            .max(10),
          rotateKey: z.boolean().optional(),
        }),
        req.body,
      );
      const companyId = req.auth!.companyId;
      if (
        body.pipelineId &&
        !(await db.pipeline.findFirst({ where: { id: body.pipelineId, companyId } }))
      )
        throw new HttpError(400, 'VALIDATION_ERROR', 'Pipeline not found');
      if (
        body.ownerId &&
        !(await db.user.findFirst({ where: { id: body.ownerId, companyId, status: 'active' } }))
      )
        throw new HttpError(400, 'VALIDATION_ERROR', 'Owner not found');
      const current = await captureView(companyId);
      const key = !current.key || body.rotateKey ? randomToken(12) : current.key;
      const value = {
        enabled: body.enabled,
        key,
        pipelineId: body.pipelineId,
        ownerId: body.ownerId,
        allowedOrigins: body.allowedOrigins.map((o) => o.replace(/\/$/, '')),
      };
      await db.setting.upsert({
        where: { companyId_key: { companyId, key: 'leadCapture' } },
        create: { companyId, key: 'leadCapture', value },
        update: { value },
      });
      await audit(db, actor(req), {
        action: 'lead_capture.updated',
        entity: 'settings',
        after: { enabled: body.enabled, rotated: !!body.rotateKey },
      });
      res.json(await captureView(companyId));
    }),
  );

  // ───────────── reports ─────────────
  router.get(
    '/crm/reports/summary',
    asyncHandler(async (req, res) => {
      need(req, 'crm.lead.view');
      const q = req.query as Record<string, string | undefined>;
      const to = q.to ?? new Date().toISOString().slice(0, 10);
      const from = q.from ?? new Date(Date.now() - 90 * 86_400_000).toISOString().slice(0, 10);
      const companyId = req.auth!.companyId;
      const rates = new Map(
        (
          await db.exchangeRate.findMany({
            where: { companyId },
            orderBy: { effectiveDate: 'asc' },
          })
        ).map((r) => [r.currency, new Decimal(r.rate.toString())]),
      );
      const base = (l: { estimatedValue: Prisma.Decimal; currency: string }) =>
        new Decimal(l.estimatedValue.toString()).times(
          l.currency === 'UGX' ? 1 : (rates.get(l.currency) ?? 1),
        );
      const leads = await db.lead.findMany({
        where: {
          companyId,
          ...ownScope(req.auth!),
          createdAt: { gte: toDate(from), lt: new Date(toDate(to).getTime() + 86_400_000) },
          ...(q.pipelineId ? { pipelineId: Number(q.pipelineId) } : {}),
        },
      });
      const stages = await db.pipelineStage.findMany({
        where: { pipeline: { companyId, ...(q.pipelineId ? { id: Number(q.pipelineId) } : {}) } },
        orderBy: [{ pipelineId: 'asc' }, { position: 'asc' }],
      });
      const hist = await db.leadStageHistory.findMany({
        where: { leadId: { in: leads.map((l) => l.id) } },
      });
      const users = await db.user.findMany({
        where: { companyId },
        select: { id: true, name: true },
      });
      const stageOf = (id: number) => stages.find((s) => s.id === id);

      const funnel = stages.map((s) => {
        const reached = new Set(hist.filter((h) => h.toStageId === s.id).map((h) => h.leadId));
        const current = leads.filter((l) => l.stageId === s.id);
        return {
          stageId: s.id,
          pipelineId: s.pipelineId,
          name: s.name,
          kind: s.kind,
          probability: s.probability,
          reached: reached.size,
          current: current.length,
          currentValue: current.reduce((t, l) => t.plus(base(l)), new Decimal(0)).toFixed(0),
        };
      });
      const open = leads.filter((l) => l.status === 'open');
      const weighted = open.reduce(
        (t, l) =>
          t.plus(
            base(l)
              .times(stageOf(l.stageId)?.probability ?? 0)
              .div(100),
          ),
        new Decimal(0),
      );
      const bySource = LEAD_SOURCES.map((src) => {
        const ls = leads.filter((l) => l.source === src);
        const won = ls.filter((l) => l.status === 'won');
        const closed = ls.filter((l) => l.status !== 'open');
        return {
          source: src,
          leads: ls.length,
          won: won.length,
          lost: ls.filter((l) => l.status === 'lost').length,
          winRate: closed.length ? Math.round((won.length / closed.length) * 100) : null,
          wonValue: won.reduce((t, l) => t.plus(base(l)), new Decimal(0)).toFixed(0),
        };
      }).filter((s) => s.leads > 0);
      const reasons = new Map<string, number>();
      for (const l of leads.filter((x) => x.status === 'lost'))
        reasons.set(
          l.lostReason ?? 'No reason',
          (reasons.get(l.lostReason ?? 'No reason') ?? 0) + 1,
        );
      const wonLeads = leads.filter((l) => l.status === 'won' && l.closedAt);
      const cycleDays = wonLeads.length
        ? wonLeads.reduce(
            (t, l) => t + (l.closedAt!.getTime() - l.createdAt.getTime()) / 86_400_000,
            0,
          ) / wonLeads.length
        : null;
      const owners = [...new Set(leads.map((l) => l.ownerId))];
      const reps = owners.map((oid) => {
        const ls = leads.filter((l) => l.ownerId === oid);
        const won = ls.filter((l) => l.status === 'won');
        const closed = ls.filter((l) => l.status !== 'open');
        return {
          ownerId: oid,
          name: users.find((u) => u.id === oid)?.name ?? 'Unassigned',
          leads: ls.length,
          open: ls.filter((l) => l.status === 'open').length,
          won: won.length,
          lost: ls.filter((l) => l.status === 'lost').length,
          winRate: closed.length ? Math.round((won.length / closed.length) * 100) : null,
          wonValue: won.reduce((t, l) => t.plus(base(l)), new Decimal(0)).toFixed(0),
        };
      });
      res.json({
        period: { from, to },
        totals: {
          leads: leads.length,
          open: open.length,
          won: leads.filter((l) => l.status === 'won').length,
          lost: leads.filter((l) => l.status === 'lost').length,
          openValue: open.reduce((t, l) => t.plus(base(l)), new Decimal(0)).toFixed(0),
          weightedValue: weighted.toFixed(0),
          avgCycleDays: cycleDays === null ? null : Math.round(cycleDays * 10) / 10,
        },
        funnel,
        sources: bySource,
        lostReasons: [...reasons.entries()]
          .map(([reason, count]) => ({ reason, count }))
          .sort((a, b) => b.count - a.count),
        reps,
      });
    }),
  );

  void ensureLeadRule;
  return router;
}
